'use client'

import { useState, type ReactNode } from 'react'
import { CreditCard } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card'
import type { BillingOverviewResponse } from '@/lib/billing/types'
import { formatCredits, formatCreditsExpiring } from '@/lib/billing/utils/credits'
import { BalanceDetailsDialog, formatExpiryDate } from './balance-details-dialog'

type Balance = BillingOverviewResponse['balance']

/** `children` renders under the amount (the Shopify plan-credits progress). */
export function BalanceCard({
  balance,
  timezone,
  children,
}: {
  balance: Balance
  timezone: string
  children?: ReactNode
}) {
  const [detailsOpen, setDetailsOpen] = useState(false)

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardDescription className="flex items-center gap-1">
            <CreditCard className="h-4 w-4" /> <span>Balance</span>
          </CardDescription>
          {balance.grants.length > 0 && (
            <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => setDetailsOpen(true)}>
              Details
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className={`break-words font-mono text-2xl font-bold ${balanceTone(balance)}`}>
          {formatCredits(balance.balanceMicrousd)}
        </div>
        {balance.nextExpiry && (
          <p className="mt-1 text-xs text-muted-foreground">{nextExpiryText(balance.nextExpiry, timezone)}</p>
        )}
        {children}
        {balance.isDepleted && <p className="mt-1 text-xs text-destructive">Balance depleted</p>}
        {balance.isLow && !balance.isDepleted && <p className="mt-1 text-xs text-yellow-600">Low balance</p>}
      </CardContent>
      <BalanceDetailsDialog
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        grants={balance.grants}
        timezone={timezone}
      />
    </Card>
  )
}

function balanceTone({ isDepleted, isLow }: Balance): string {
  if (isDepleted) return 'text-destructive'
  if (isLow) return 'text-yellow-600'
  return ''
}

function nextExpiryText({ expiresAt, amountMicrousd }: NonNullable<Balance['nextExpiry']>, timezone: string): string {
  return formatCreditsExpiring(amountMicrousd, formatExpiryDate(expiresAt, timezone))
}
